import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimatedWaveDivider } from '../components/WaveDivider';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

export const ResourcePostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (err) {
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        url: window.location.href
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1E3A8A] border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <h1 className="text-3xl font-bold text-[#0F172A] mb-4">Resource Not Found</h1>
        <p className="text-slate-600 mb-8">The article you're looking for doesn't exist.</p>
        <Link to="/resources">
          <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Resources
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative bg-[#1E3A8A] py-16 md:py-24" data-testid="resource-post-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div {...fadeInUp}>
            <Link 
              to="/resources"
              className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-6 transition-colors"
              data-testid="resource-post-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Resources
            </Link>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-6">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-blue-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.created_at)}</span>
              </div>
              <button 
                onClick={sharePost}
                className="flex items-center gap-2 hover:text-white transition-colors"
                data-testid="resource-post-share-btn"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </motion.div>
        </div>
        <AnimatedWaveDivider variant="bottom" fromColor="primary" toColor="white" className="absolute bottom-0 left-0 right-0" />
      </section>

      {/* Content */}
      <section className="bg-white py-16 md:py-24" data-testid="resource-post-content">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {post.featured_image && (
            <motion.img 
              {...fadeInUp}
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-2xl mb-12"
            />
          )}
          
          <motion.div 
            {...fadeInUp}
            className="blog-content prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* CTA within post */}
          <motion.div 
            {...fadeInUp}
            className="mt-12 bg-[#1E3A8A] rounded-2xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Try Unlimited Washes?
            </h3>
            <p className="text-blue-100 mb-6">
              Join EverWash and wash your car as often as you want for one low monthly price.
            </p>
            <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer" data-testid="resource-post-everwash-cta">
              <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold px-8 py-3 rounded-full">
                Join EverWash
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* More Posts CTA */}
      <section className="bg-slate-50 py-16 md:py-20" data-testid="resource-post-more">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
              Want More Car Care Tips?
            </h2>
            <p className="text-slate-600 mb-6">
              Check out our other articles for more helpful advice.
            </p>
            <Link to="/resources" data-testid="resource-post-view-all-btn">
              <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white font-bold px-8 py-3 rounded-full">
                View All Resources
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResourcePostPage;
